import {ArrowLeftOutlined, ArrowRightOutlined, CheckOutlined, LockOutlined, MailOutlined} from '@ant-design/icons';
import {Button, Form, Input} from 'antd';
import {useState} from 'react';
import toast from 'react-hot-toast';
import {useNavigate} from 'react-router-dom';

import {RQForgotPasswordAPI, RQResetPasswordAPI} from '../../reactQuery/apis';

// eslint-disable-next-line react/prop-types
const ResetPasswordComponent = ({email = '', onResetSuccess}) => {
  const [stage, setStage] = useState('email');
  const [loading, setLoading] = useState(false);
  const [resetCode, setResetCode] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [pwStrength, setPwStrength] = useState(0);

  const [form] = Form.useForm();
  const navigate = useNavigate();

  const {mutate: forgotPassword} = RQForgotPasswordAPI();
  const {mutate: resetPassword} = RQResetPasswordAPI({
    onSuccess: response => {
      setLoading(false);
      if (response) {
        toast.success('Password reset successful');
        form.resetFields();
        setStage('success');
        if (onResetSuccess) {onResetSuccess();}
      } else {
        toast.error(response?.message || 'Something went wrong');
      }
    },
    onError: error => {
      setLoading(false);
      toast.error(error?.message || 'Something went wrong');
    },
  });

  const calcStrength = value => {
    let s = 0;
    if (value.length >= 6) {s++;}
    if (value.length >= 10) {s++;}
    if (/[A-Z]/.test(value) && /[0-9]/.test(value)) {s++;}
    if (/[^A-Za-z0-9]/.test(value)) {s++;}
    return s;
  };

  const strengthColor = ['#E24B4A', '#EF9F27', '#1D9E75', '#0F6E56'];
  const strengthLabel = ['Weak', 'Fair', 'Good', 'Strong'];

  const handleEmailSubmit = values => {
    setLoading(true);
    forgotPassword(
      {email: values.email},
      {
        onSuccess: () => {
          toast.success('Reset code sent to email.');
          setLoading(false);
          setStage('code');
        },
        onError: err => {
          setLoading(false);
          toast.error(err?.response?.data?.message || 'Something went wrong');
        },
      },
    );
  };

  const handleCodeSubmit = () => {
    if (!resetCode.trim()) {
      toast.error('Please enter the reset code');
      return;
    }
    setStage('password');
  };

  const handlePasswordSubmit = values => {
    setLoading(true);
    resetPassword({
      formData: {
        email: form.getFieldValue('email'),
        resetCode,
        newPassword: values.newPassword,
        confirmPassword: values.confirmPassword,
      },
    });
  };

  const stepIndex = {email: 1, code: 2, password: 3, success: 3};
  const currentStep = stepIndex[stage];

  const StepBar = () => (
    <div className={'flex gap-1.5 mb-6'}>
      {[1, 2, 3].map(n => (
        <div
          key={n}
          className={'flex-1 h-0.5 rounded-full transition-all duration-300'}
          style={{background: n <= currentStep ? '#20457e' : '#d3dbe8'}}
        />
      ))}
    </div>
  );

  const headers = {
    email: {title: 'Reset your password', sub: 'Enter your email and we\'ll send a reset code.'},
    code: {title: 'Enter reset code', sub: `We sent a code to ${form.getFieldValue('email') || 'your email'}.`},
    password: {title: 'Create new password', sub: 'Choose something strong and memorable.'},
    success: {title: 'All done!', sub: 'Your account is secured with your new password.'},
  };

  return (
    <div className={'flex justify-center px-4'}>
      <div className={'w-full  '}>

        {/* Card header */}
        <div className={'flex gap-2 '}>
          <div className={'w-11 h-11 rounded-full bg-gray-100 border border-gray-200 flex items-center justify-center mb-4'}>
            {stage === 'success'
              ? <CheckOutlined style={{fontSize: 18, color: '#059669'}} />
              : <LockOutlined style={{fontSize: 18, color: '#6B7280'}} />
            }
          </div>
          <div>
            <h2 className={'header mb-1'}>{headers[stage].title}</h2>
            <p className={'text-sm text-gray leading-relaxed'}>{headers[stage].sub}</p>
          </div>
        </div>

        {/* Card body */}
        <div className={'px-8 py-6'}>
          <StepBar />

          {/* ── Stage: Email ── */}
          {stage === 'email' && (
            <Form form={form} layout={'vertical'} onFinish={handleEmailSubmit} initialValues={{email}}>
              <Form.Item
                name={'email'}
                label={<span className={'text-xs font-medium text-gray-500 uppercase tracking-wide'}>Email</span>}
                rules={[
                  {required: true, message: 'Email is required'},
                  {type: 'email', message: 'Invalid email format'},
                  {
                    validator: (_, value) =>
                      value && value !== value.toLowerCase()
                        ? Promise.reject('Email must be in lowercase')
                        : Promise.resolve(),
                  },
                ]}
              >
                <Input
                  prefix={<MailOutlined className={'text-gray-400'} />}
                  placeholder={'you@example.com'}
                  size={'large'}
                  className={'rounded-lg'}
                />
              </Form.Item>
              <Form.Item className={'mb-0 mt-4'}>
                <Button
                  htmlType={'submit'}
                  loading={loading}
                  block
                  size={'large'}
                  className={'btn-primary gap-2'}
                  icon={<ArrowRightOutlined />}
                  iconPosition={'end'}
                >
                  Send reset code
                </Button>
              </Form.Item>
            </Form>
          )}

          {/* ── Stage: Code ── */}
          {stage === 'code' && (
            <div>
              <div className={'mb-1'}>
                <span className={'text-xs font-medium text-gray-500 uppercase tracking-wide'}>Reset code</span>
              </div>
              <Input
                value={resetCode}
                onChange={e => setResetCode(e.target.value)}
                onPressEnter={handleCodeSubmit}
                placeholder={'Enter reset code'}
                size={'large'}
                className={'rounded-lg mb-2'}
                // autoFocus
              />
              
              <Button
                block size={'large'}
                onClick={handleCodeSubmit}
                className={'btn-primary'}
                icon={<ArrowRightOutlined />}
                iconPosition={'end'}
              >
                Verify code
              </Button>
              <button
                type={'button'}
                onClick={() => setStage('email')}
                className={'mt-3 flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors'}
              >
                <ArrowLeftOutlined style={{fontSize: 12}} /> Back
              </button>
            </div>
          )}

          {stage === 'password' && (
            <Form form={form} layout={'vertical'} onFinish={handlePasswordSubmit}>
              <Form.Item
                name={'newPassword'}
                label={<span className={'text-xs font-medium text-gray-500 uppercase tracking-wide'}>New password</span>}
                rules={[
                  {required: true, message: 'Password is required'},
                  {min: 6, message: 'Password must be at least 6 characters'},
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className={'text-gray-400'} />}
                  placeholder={'Min. 6 characters'}
                  className={'rounded-lg'}
                  onChange={e => setPwStrength(calcStrength(e.target.value))}
                  visibilityToggle={{visible: showNew, onVisibleChange: setShowNew}}
                />
              </Form.Item>

              <Form.Item
                name={'confirmPassword'}
                label={<span className={'text-xs font-medium text-primary-800 uppercase tracking-wide'}>Confirm password</span>}
                dependencies={['newPassword']}
                rules={[
                  {required: true, message: 'Please confirm your password'},
                  ({getFieldValue}) => ({
                    validator(_, value) {
                      if (!value || getFieldValue('newPassword') === value) {return Promise.resolve();}
                      return Promise.reject('Passwords do not match');
                    },
                  }),
                ]}
              >
                <Input.Password
                  prefix={<LockOutlined className={'text-gray-400'} />}
                  placeholder={'Repeat new password'}
                  className={'rounded-lg'}
                  visibilityToggle={{visible: showConfirm, onVisibleChange: setShowConfirm}}
                />
              </Form.Item>

              <Form.Item className={'mb-0 mt-4'}>
                <Button
                  htmlType={'submit'}
                  loading={loading}
                  block
                  size={'large'}
                  className={'btn-primary'}
                  icon={<CheckOutlined />}
                  iconPosition={'end'}
                >
                  Reset password
                </Button>
              </Form.Item>
              <button
                type={'button'}
                onClick={() => setStage('code')}
                className={'mt-3 flex items-center gap-1.5 text-sm text-gray-400 hover:text-gray-700 transition-colors'}
              >
                <ArrowLeftOutlined style={{fontSize: 12}} /> Back
              </button>
            </Form>
          )}

          {/* ── Stage: Success ── */}
          {stage === 'success' && (
            <div className={'text-center py-4'}>
              <div className={'w-14 h-14 rounded-full bg-emerald-50 border border-emerald-100 flex items-center justify-center mx-auto mb-4'}>
                <CheckOutlined style={{fontSize: 22, color: '#059669'}} />
              </div>
              <p className={'text-sm font-medium text-gray-800 mb-1'}>Password updated</p>
              <p className={'text-sm text-gray-500 mb-6'}>You can now sign in with your new password.</p>
              <Button
                block
                size={'large'}
                onClick={() => navigate('/login')}
                className={'rounded-lg font-medium'}
                style={{background: '#111827', color: '#fff', border: 'none'}}
                icon={<ArrowRightOutlined />}
                iconPosition={'end'}
              >
                Back to sign in
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ResetPasswordComponent;
